import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useI18n } from "@/lib/I18nContext";
import { pickText } from "@/lib/bilingual";
import Layout from "@/components/Layout";
import PublicShell from "@/components/PublicShell";

const PLACEHOLDER_IMG =
  "https://images.pexels.com/photos/34649361/pexels-photo-34649361.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function resolveImage(url) {
  if (!url) return PLACEHOLDER_IMG;
  if (url.startsWith("http")) return url;
  // Backend returned a relative /api/files/... path
  const base = API_BASE.replace(/\/api$/, "");
  return `${base}${url}`;
}

function ArticleView({ item }) {
  const { lang } = useI18n();
  const title = pickText(item.title, lang);
  const body = pickText(item.body, lang);

  useEffect(() => {
    document.title = `${title} | La Polla Mundialista`;
    let m = document.querySelector('meta[name="description"]');
    if (!m) {
      m = document.createElement("meta");
      m.setAttribute("name", "description");
      document.head.appendChild(m);
    }
    const excerpt = (body || "").slice(0, 160).replace(/\s+/g, " ").trim();
    m.setAttribute("content", excerpt || "Noticia del FIFA World Cup 2026 — La Polla Mundialista.");
    return () => {
      document.title = "La Polla Mundialista";
    };
  }, [title, body]);

  const dateLocale = lang === "en" ? "en-US" : "es-MX";

  return (
    <article className="max-w-3xl mx-auto" data-testid={`news-detail-${item.id}`}>
      <Link
        to="/noticias"
        data-testid="back-to-news"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-[var(--c-lime)] mb-6"
      >
        <ArrowLeft size={14} /> {lang === "en" ? "Back to News" : "Volver a Noticias"}
      </Link>

      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono-data mb-3">
        {new Date(item.created_at).toLocaleDateString(dateLocale, {
          year: "numeric",
          month: "short",
          day: "2-digit",
        })}
        {item.author_nickname && <> · {item.author_nickname}</>}
      </div>

      <h1
        data-testid="news-detail-title"
        className="font-display text-4xl md:text-5xl uppercase leading-none mb-6"
      >
        {title}
      </h1>

      <div className="aspect-[16/9] overflow-hidden bg-zinc-900 mb-8 border border-zinc-800">
        <img
          src={resolveImage(item.image_url)}
          alt={title}
          className="w-full h-full object-cover"
        />
      </div>

      <div
        data-testid="news-detail-body"
        className="text-zinc-200 text-base md:text-lg leading-relaxed whitespace-pre-line"
      >
        {body}
      </div>
    </article>
  );
}

export default function NoticiaDetalle() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/news/${id}`)
      .then((r) => {
        if (alive) setItem(r.data);
      })
      .catch((e) => {
        if (alive) setError(e?.response?.data?.detail || "No se pudo cargar la noticia");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  const content = loading ? (
    <div className="text-center text-zinc-500 text-sm py-16">Cargando…</div>
  ) : error ? (
    <div className="max-w-3xl mx-auto bg-zinc-900 border border-zinc-800 p-12 text-center text-zinc-400">
      {error}
      <div className="mt-6">
        <Link to="/noticias" className="text-[var(--c-lime)] uppercase tracking-widest text-xs">
          ← Volver a Noticias
        </Link>
      </div>
    </div>
  ) : item ? (
    <ArticleView item={item} />
  ) : null;

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="font-display text-2xl uppercase tracking-wider">Cargando…</div>
      </div>
    );
  }

  if (user) {
    return <Layout>{content}</Layout>;
  }
  return (
    <PublicShell forcePublicNews>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{content}</div>
    </PublicShell>
  );
}
